import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import api, { SOCKET_URL } from './api';

const SENDER_COLORS = [
  '#7B2FF7', '#E040A0', '#43a047', '#8e24aa',
  '#f57c00', '#00838f', '#9C27B0', '#546e7a',
  '#d81b60', '#6A1FE0', '#5e35b1', '#ef6c00',
];

function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

export function getSenderColor(id) {
  return SENDER_COLORS[hashCode(id || '') % SENDER_COLORS.length];
}

export function formatTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

export function formatChatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export function formatDateSeparator(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Сегодня';
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function getInitials(name) {
  if (!name) return '??';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 0.3);
    });
  } catch {}
}

export function useChat(user) {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('terka_sound') !== 'false');
  const [typingUsers, setTypingUsers] = useState({});
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [socket, setSocketState] = useState(null);

  const socketRef = useRef(null);
  const selectedRoomRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const tempIdCounter = useRef(0);
  const typingTimeouts = useRef({});
  const searchDebounce = useRef(null);

  selectedRoomRef.current = selectedRoom;

  useEffect(() => { localStorage.setItem('terka_sound', soundEnabled); }, [soundEnabled]);

  // Socket connection
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('terka_token');
    if (!token) return;

    const socket = io(SOCKET_URL, { auth: { token }, transports: ['websocket'], reconnection: true });
    socketRef.current = socket;
    setSocketState(socket);

    socket.on('new_message', (msg) => {
      const cur = selectedRoomRef.current;
      if (msg.chatId === cur?.id) {
        setMessages(prev => {
          const filtered = prev.filter(m => !m.id.startsWith('temp_'));
          if (filtered.some(m => m.id === msg.id)) return filtered;
          return [...filtered, msg];
        });
        api.post(`/chats/${msg.chatId}/read`).catch(() => {});
      } else if (soundEnabled) {
        playNotificationSound();
      }
      loadRooms();
    });

    socket.on('message_deleted', ({ chatId, messageId }) => {
      if (chatId === selectedRoomRef.current?.id) {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, isDeleted: true, content: null } : m));
      }
    });

    socket.on('message_edited', ({ chatId, messageId, content }) => {
      if (chatId === selectedRoomRef.current?.id) {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, content, isEdited: true } : m));
      }
    });

    socket.on('message_pinned', ({ chatId, messageId, isPinned }) => {
      if (chatId === selectedRoomRef.current?.id) {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, isPinned } : m));
      }
    });

    socket.on('user_typing', ({ chatId, userId, displayName }) => {
      if (chatId === selectedRoomRef.current?.id && userId !== user.id) {
        setTypingUsers(prev => ({ ...prev, [userId]: displayName }));
        if (typingTimeouts.current[userId]) clearTimeout(typingTimeouts.current[userId]);
        typingTimeouts.current[userId] = setTimeout(() => {
          setTypingUsers(prev => { const n = { ...prev }; delete n[userId]; return n; });
        }, 3000);
      }
    });

    socket.on('user_stop_typing', ({ chatId, userId }) => {
      if (chatId === selectedRoomRef.current?.id) {
        setTypingUsers(prev => { const n = { ...prev }; delete n[userId]; return n; });
      }
    });

    return () => { socket.disconnect(); socketRef.current = null; setSocketState(null); };
  }, [user]);

  // Load rooms
  const loadRooms = useCallback(async () => {
    try {
      const { data } = await api.get('/chats');
      if (data.success) setRooms(data.chats);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) loadRooms(); }, [user, loadRooms]);

  // Load messages
  const loadMessages = useCallback(async (roomId) => {
    setLoadingMessages(true);
    try {
      const { data } = await api.get(`/chats/${roomId}/messages`, { params: { limit: 100 } });
      if (data.success) setMessages(data.messages);
      api.post(`/chats/${roomId}/read`).catch(() => {});
      socketRef.current?.emit('join_chat', { chatId: roomId });
    } catch {} finally { setLoadingMessages(false); }
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      loadMessages(selectedRoom.id);
      setReplyTo(null);
      setEditingMessage(null);
      setShowSearch(false);
      setSearchTerm('');
    } else {
      setMessages([]);
    }
  }, [selectedRoom, loadMessages]);

  // Auto-scroll: instant jump on first load, smooth for new messages
  const justLoadedRoom = useRef(false);
  useEffect(() => {
    if (!messagesEndRef.current) return;
    if (justLoadedRoom.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
      justLoadedRoom.current = false;
    } else {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => { justLoadedRoom.current = true; }, [selectedRoom]);

  // Send message
  const sendMessage = useCallback(async () => {
    if (!selectedRoom || !messageText.trim()) return;
    const content = messageText.trim();
    setMessageText('');

    // If editing
    if (editingMessage) {
      try {
        await api.put(`/chats/${selectedRoom.id}/messages/${editingMessage.id}`, { content });
        setMessages(prev => prev.map(m => m.id === editingMessage.id ? { ...m, content, isEdited: true } : m));
      } catch {}
      setEditingMessage(null);
      return;
    }

    const tempId = `temp_${Date.now()}_${tempIdCounter.current++}`;
    setMessages(prev => [...prev, {
      id: tempId, chatId: selectedRoom.id, senderId: user.id, content, type: 'text',
      sender: { id: user.id, displayName: user.displayName, avatar: user.avatar },
      createdAt: new Date().toISOString(), status: 'sending',
      isDeleted: false, isEdited: false, isPinned: false,
      replyTo: replyTo?.id || null,
      replyMessage: replyTo ? { id: replyTo.id, content: replyTo.content, type: replyTo.type, sender: replyTo.sender } : null,
    }]);

    setReplyTo(null);

    try {
      const { data } = await api.post(`/chats/${selectedRoom.id}/messages`, { content, replyTo: replyTo?.id });
      if (data.success) {
        setMessages(prev => prev.map(m => m.id === tempId ? { ...data.message, status: 'sent' } : m));
      }
    } catch {
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: 'failed' } : m));
    }
    loadRooms();
    textareaRef.current?.focus();
  }, [selectedRoom, messageText, user, loadRooms, replyTo, editingMessage]);

  // Upload file
  const uploadFile = useCallback(async (file) => {
    if (!selectedRoom) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/chats/${selectedRoom.id}/messages/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
      });
      loadRooms();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.quotaExceeded) {
        alert(data.message || 'Превышен лимит хранилища');
      } else {
        console.error('Upload failed:', err);
        alert('Не удалось отправить файл');
      }
    }
  }, [selectedRoom, loadRooms]);

  // Delete message
  const deleteMessage = useCallback(async (messageId) => {
    if (!selectedRoom) return;
    try { await api.delete(`/chats/${selectedRoom.id}/messages/${messageId}`); } catch {}
  }, [selectedRoom]);

  // Edit message
  const startEditing = useCallback((msg) => {
    setEditingMessage(msg);
    setMessageText(msg.content || '');
    setReplyTo(null);
    textareaRef.current?.focus();
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingMessage(null);
    setMessageText('');
  }, []);

  // Reply
  const startReply = useCallback((msg) => {
    setReplyTo(msg);
    setEditingMessage(null);
    textareaRef.current?.focus();
  }, []);

  const cancelReply = useCallback(() => { setReplyTo(null); }, []);

  // Forward
  const forwardMessage = useCallback(async (messageId, targetChatId) => {
    if (!selectedRoom) return;
    try {
      await api.post(`/chats/${selectedRoom.id}/messages/${messageId}/forward`, { targetChatId });
      loadRooms();
    } catch (err) { console.error('Forward failed:', err); }
  }, [selectedRoom, loadRooms]);

  // Pin
  const pinMessage = useCallback(async (messageId) => {
    if (!selectedRoom) return;
    try { await api.post(`/chats/${selectedRoom.id}/messages/${messageId}/pin`); } catch {}
  }, [selectedRoom]);

  // Search
  const searchMessages = useCallback(async (term) => {
    setSearchTerm(term);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!term || term.length < 2 || !selectedRoom) { setSearchResults([]); return; }
    searchDebounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/chats/${selectedRoom.id}/messages/search`, { params: { q: term } });
        if (data.success) setSearchResults(data.messages.map(m => m.id));
      } catch {}
    }, 300);
  }, [selectedRoom]);

  const toggleSearch = useCallback(() => {
    setShowSearch(prev => !prev);
    setSearchTerm('');
    setSearchResults([]);
  }, []);

  // Toggle mute
  const toggleMute = useCallback(async (roomId) => {
    try { await api.patch(`/chats/${roomId}/mute`); loadRooms(); } catch {}
  }, [loadRooms]);

  // Create room
  const createRoom = useCallback(async (name, type, memberIds) => {
    try {
      const { data } = await api.post('/chats', { name, type, memberIds });
      if (data.success) {
        // Reload rooms and pick the enriched version
        const { data: roomsData } = await api.get('/chats');
        if (roomsData.success) {
          setRooms(roomsData.chats);
          const enriched = roomsData.chats.find(r => r.id === data.chat.id);
          setSelectedRoom(enriched || data.chat);
        } else {
          setSelectedRoom(data.chat);
        }
      }
      return data;
    } catch (err) { throw err; }
  }, []);

  // Send typing
  const sendTyping = useCallback(() => {
    if (selectedRoom && socketRef.current) socketRef.current.emit('typing', { chatId: selectedRoom.id });
  }, [selectedRoom]);

  // Key handler
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    if (e.key === 'Escape') { cancelEditing(); cancelReply(); }
  }, [sendMessage, cancelEditing, cancelReply]);

  return {
    socket,
    socketRef,
    rooms, selectedRoom, messages, messageText, loading, loadingMessages,
    soundEnabled, typingUsers, replyTo, editingMessage,
    searchTerm, searchResults, showSearch,
    messagesEndRef, textareaRef,
    setSelectedRoom, setMessageText, setSoundEnabled,
    sendMessage, uploadFile, deleteMessage, toggleMute, createRoom,
    startEditing, cancelEditing, startReply, cancelReply,
    forwardMessage, pinMessage,
    searchMessages, toggleSearch,
    handleKeyPress, sendTyping, loadRooms,
    user,
  };
}
